import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "@/features/auth/AuthProvider";
import { ProtectedRoute } from "@/features/auth/ProtectedRoute";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { AppLayout } from "@/components/layout/AppLayout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useAuth } from "@/features/auth/useAuth";
import { canManageRecruiting, canManageUsers, canViewActivityLog, canViewKpis, canViewOperations } from "@/features/auth/types";

import Home from "@/pages/public/Home";
import HeroPreview from "@/pages/public/HeroPreview";
import About from "@/pages/public/About";
import People from "@/pages/public/People";
import Services from "@/pages/public/Services";
import ServiceDetail from "@/pages/public/ServiceDetail";
import Industries from "@/pages/public/Industries";
import Track from "@/pages/public/Track";
import Careers from "@/pages/public/Careers";
import Apply from "@/pages/public/Apply";
import Contact from "@/pages/public/Contact";
import Blog from "@/pages/public/Blog";
import BlogPost from "@/pages/public/BlogPost";
import { Privacy, Terms } from "@/pages/public/Legal";
import NotFound from "@/pages/public/NotFound";

import Login from "@/pages/app/Login";
import CustomerDashboard from "@/pages/app/customer/Dashboard";
import DriverDashboard from "@/pages/app/driver/Dashboard";
import CarrierDashboard from "@/pages/app/carrier/Dashboard";
import InternalDashboard from "@/pages/app/internal/Dashboard";
import InternalLoads from "@/pages/app/internal/Loads";
import InternalFleet from "@/pages/app/internal/Fleet";
import InternalApplications from "@/pages/app/internal/Applications";
import InternalMessages from "@/pages/app/internal/Messages";
import InternalJobs from "@/pages/app/internal/Jobs";
import InternalUsers from "@/pages/app/internal/Users";
import InternalDocuments from "@/pages/app/internal/Documents";
import InternalActivityLog from "@/pages/app/internal/ActivityLog";
import InternalKpis from "@/pages/app/internal/Kpis";
import InternalKpiSection from "@/pages/app/internal/KpiSection";
import InternalKpiScorecard from "@/pages/app/internal/KpiScorecard";

const internalNav = [
  { label: "Overview", to: "/app/internal" },
  { label: "Loads", to: "/app/internal/loads" },
  { label: "Fleet", to: "/app/internal/fleet" },
  { label: "Applications", to: "/app/internal/applications" },
  { label: "Messages", to: "/app/internal/messages" },
  { label: "Documents", to: "/app/internal/documents" },
  { label: "Jobs", to: "/app/internal/jobs" },
  { label: "Users", to: "/app/internal/users" },
  { label: "Activity", to: "/app/internal/activity" },
  { label: "KPIs", to: "/app/internal/kpis" },
];

/** Staff layout that hides admin/recruiting links from member-level staff. */
function InternalLayout() {
  const { user } = useAuth();
  const nav = internalNav.filter((item) => {
    if (item.to === "/app/internal/users") return canManageUsers(user);
    if (item.to === "/app/internal/activity") return canViewActivityLog(user);
    if (item.to === "/app/internal/jobs") {
      return canManageRecruiting(user);
    }
    if (item.to === "/app/internal/loads" || item.to === "/app/internal/fleet") {
      return canViewOperations(user);
    }
    if (item.to === "/app/internal/kpis") return canViewKpis(user);
    return true;
  });
  return <AppLayout title="Staff" nav={nav} />;
}

/** Blocks direct-URL access to the operational views (Loads/Fleet) for staff who
 *  can't see them (HR). UX guard only; server RLS/functions remain the boundary. */
function OperationsRoute() {
  const { user } = useAuth();
  if (!canViewOperations(user)) return <Navigate to="/app/internal" replace />;
  return <Outlet />;
}

/** Admin-only KPI pages. UX guard; `kpi_entries` RLS is the real boundary. */
function KpiRoute() {
  const { user } = useAuth();
  if (!canViewKpis(user)) return <Navigate to="/app/internal" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          {/* Public marketing site */}
          <Route element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="/preview/hero" element={<HeroPreview />} />
            <Route path="/about" element={<About />} />
            <Route path="/people" element={<People />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/track" element={<Track />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Auth */}
          <Route path="/app" element={<Login />} />

          {/* Customer portal */}
          <Route element={<ProtectedRoute allow={["customer"]} />}>
            <Route element={<AppLayout title="Customer" nav={[{ label: "Shipments", to: "/app/customer" }]} />}>
              <Route path="/app/customer" element={<CustomerDashboard />} />
            </Route>
          </Route>

          {/* Driver portal */}
          <Route element={<ProtectedRoute allow={["driver"]} />}>
            <Route element={<AppLayout title="Driver" nav={[{ label: "My loads", to: "/app/driver" }]} />}>
              <Route path="/app/driver" element={<DriverDashboard />} />
            </Route>
          </Route>

          {/* Carrier portal */}
          <Route element={<ProtectedRoute allow={["carrier"]} />}>
            <Route element={<AppLayout title="Carrier" nav={[{ label: "Available loads", to: "/app/carrier" }]} />}>
              <Route path="/app/carrier" element={<CarrierDashboard />} />
            </Route>
          </Route>

          {/* Internal dashboard */}
          <Route element={<ProtectedRoute allow={["internal"]} />}>
            <Route element={<InternalLayout />}>
              <Route path="/app/internal" element={<InternalDashboard />} />
              <Route element={<OperationsRoute />}>
                <Route path="/app/internal/loads" element={<InternalLoads />} />
                <Route path="/app/internal/fleet" element={<InternalFleet />} />
              </Route>
              <Route path="/app/internal/applications" element={<InternalApplications />} />
              <Route path="/app/internal/messages" element={<InternalMessages />} />
              <Route path="/app/internal/documents" element={<InternalDocuments />} />
              <Route path="/app/internal/jobs" element={<InternalJobs />} />
              <Route path="/app/internal/users" element={<InternalUsers />} />
              <Route path="/app/internal/activity" element={<InternalActivityLog />} />
              <Route element={<KpiRoute />}>
                <Route path="/app/internal/kpis" element={<InternalKpis />} />
                <Route path="/app/internal/kpis/section/:department" element={<InternalKpiSection />} />
                <Route path="/app/internal/kpis/scorecard" element={<InternalKpiScorecard />} />
              </Route>
            </Route>
          </Route>

          <Route path="/app/*" element={<Navigate to="/app" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
